import { z } from 'zod';
import type { BoundingBox, PropertyType, SearchParams, SortOption } from '@/types/listing';
import { titleCaseCity } from './utils';

/**
 * URL search params are the source of truth for search filters.
 * This codec parses/serializes them symmetrically for the server page,
 * the client island, and canonical URL generation.
 */

const PROPERTY_TYPES: PropertyType[] = [
  'single_family',
  'condo',
  'townhouse',
  'multi_family',
  'mobile',
  'land',
];

/**
 * Which panes the results page shows. This is presentation, not a filter — but
 * it belongs in the URL all the same, because "here's the map of what I found"
 * is a link people send each other.
 *
 * It was a `useState('split')` that no URL ever reached, so `?view=map`,
 * `?view=list` and `?view=split` rendered byte-identical HTML (verified live on
 * five values, all reporting `Split view` pressed). The owner's own `?view=map`
 * link opened on the split view — so when a separate card-photo bug blanked the
 * tiles beside the map, the report came back as "the map is broken". (W13 R121)
 */
export const SEARCH_VIEWS = ['split', 'list', 'map'] as const;
export type SearchView = (typeof SEARCH_VIEWS)[number];
export const DEFAULT_VIEW: SearchView = 'split';

const SORTS: SortOption[] = [
  'price_asc',
  'price_desc',
  'newest',
  'sqft',
  'beds_asc',
  'beds_desc',
  'ppsf_asc',
  'ppsf_desc',
  'relevance',
  'open_house',
  'price_cut',
];

export const searchFiltersSchema = z.object({
  // A pasted paragraph failed `.max(120)` and got `.catch`-ed to undefined: the
  // URL still read `q=<262 characters>` while the page showed **all 8,594
  // homes**, with no chip and nothing to say the query had been thrown away.
  // Clamping instead runs a real search on the first 120 characters, and the
  // chip then shows exactly what was searched. (W12 R120b)
  q: z
    .string()
    .trim()
    .min(1)
    .transform((s) => s.slice(0, 120))
    .optional()
    .catch(undefined),
  cities: z.string().optional().catch(undefined),
  priceMin: z.coerce.number().int().positive().optional().catch(undefined),
  priceMax: z.coerce.number().int().positive().optional().catch(undefined),
  bedsMin: z.coerce.number().int().min(1).max(10).optional().catch(undefined),
  bathsMin: z.coerce.number().min(0.5).max(10).optional().catch(undefined),
  sqftMin: z.coerce.number().int().positive().optional().catch(undefined),
  sqftMax: z.coerce.number().int().positive().optional().catch(undefined),
  types: z.string().optional().catch(undefined),
  yearMin: z.coerce.number().int().min(1800).max(2030).optional().catch(undefined),
  /** "1" limits results to homes with an upcoming open house. */
  oh: z.literal('1').optional().catch(undefined),
  /** "1" limits results to homes priced below their original list price. */
  cut: z.literal('1').optional().catch(undefined),
  /** Maximum monthly HOA fee; 0 means "no HOA". */
  hoaMax: z.coerce.number().int().min(0).max(100000).optional().catch(undefined),
  /**
   * Maximum days since the home was listed. Named `domMax` for the API's
   * `dom_max`, but the API derives it from `list_date` (the feed's DaysOnMarket
   * is empty), so the UI says "Listed within" / "Listed ≤ Nd", never "on market".
   */
  domMax: z.coerce.number().int().positive().max(3650).optional().catch(undefined),
  /** Comma list of 5-digit ZIP codes, at most 10. */
  zips: z
    .string()
    .regex(/^\d{5}(,\d{5}){0,9}$/)
    .optional()
    .catch(undefined),
  /** Map area as "minLng,minLat,maxLng,maxLat" (set by "Search this area"). */
  bbox: z
    .string()
    .regex(/^-?\d{1,3}(\.\d+)?(,-?\d{1,3}(\.\d+)?){3}$/)
    .optional()
    .catch(undefined),
  sort: z.enum(SORTS as [SortOption, ...SortOption[]]).default('newest').catch('newest'),
  page: z.coerce.number().int().min(0).max(500).default(0).catch(0),
  /**
   * Results layout: `split` (list + map), `list`, or `map`. An unknown or empty
   * value falls back to `split` rather than rendering nothing.
   */
  view: z.enum(SEARCH_VIEWS).default(DEFAULT_VIEW).catch(DEFAULT_VIEW),
});

export type SearchFilters = z.infer<typeof searchFiltersSchema>;

export const DEFAULT_FILTERS: SearchFilters = searchFiltersSchema.parse({});

type RawParams = Record<string, string | string[] | undefined>;

function first(v: string | string[] | undefined): string | undefined {
  return Array.isArray(v) ? v[0] : v;
}

/** Parse Next.js searchParams into validated filters. Invalid values fall back to defaults. */
export function parseSearchParams(raw: RawParams): SearchFilters {
  return searchFiltersSchema.parse({
    q: first(raw.q),
    cities: first(raw.cities),
    priceMin: first(raw.priceMin),
    priceMax: first(raw.priceMax),
    bedsMin: first(raw.bedsMin),
    bathsMin: first(raw.bathsMin),
    sqftMin: first(raw.sqftMin),
    sqftMax: first(raw.sqftMax),
    types: first(raw.types),
    yearMin: first(raw.yearMin),
    oh: first(raw.oh),
    cut: first(raw.cut),
    hoaMax: first(raw.hoaMax),
    domMax: first(raw.domMax),
    zips: first(raw.zips),
    bbox: first(raw.bbox),
    sort: first(raw.sort),
    page: first(raw.page),
    view: first(raw.view),
  });
}

/** Parse the bbox param into API shape; degenerate or out-of-range boxes → undefined. */
export function bboxFromFilters(filters: SearchFilters): BoundingBox | undefined {
  if (!filters.bbox) return undefined;
  const [minLng, minLat, maxLng, maxLat] = filters.bbox.split(',').map(Number);
  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return undefined;
  if (minLng >= maxLng || minLat >= maxLat) return undefined;
  if (Math.abs(minLat) > 90 || Math.abs(maxLat) > 90) return undefined;
  if (Math.abs(minLng) > 180 || Math.abs(maxLng) > 180) return undefined;
  return {
    topLeft: { lat: maxLat, lon: minLng },
    bottomRight: { lat: minLat, lon: maxLng },
  };
}

/** Serialize map bounds to the bbox URL param (~11 m precision). */
export function bboxToParam(b: BoundingBox): string {
  const r = (n: number) => String(Math.round(n * 1e4) / 1e4);
  return [r(b.topLeft.lon), r(b.bottomRight.lat), r(b.bottomRight.lon), r(b.topLeft.lat)].join(',');
}

export function citySlugsFromFilters(filters: SearchFilters): string[] {
  return (filters.cities ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Where this search is looking, for prose: named cities, or the whole region. */
export function searchWhere(filters: SearchFilters): string {
  const cityNames = citySlugsFromFilters(filters).map((slug) =>
    titleCaseCity(slug.replace(/-/g, ' '))
  );
  return cityNames.length > 0 ? cityNames.join(', ') : 'the Bay Area';
}

/**
 * The title of a search page — one function, so the tab and the `<head>` cannot
 * disagree about how many homes there are.
 *
 * R13 shipped the live count by assigning `document.title` from client state.
 * That is inert on a hard navigation: React re-asserts the route's
 * `generateMetadata` title from the RSC tree after hydration and the count is
 * gone again — reproduced on both deployed origins, with the page correctly
 * rendering "1,063 homes" beside a tab reading "Homes for sale in San Jose"
 * (W15 R148, flagged there and fixed here). So the **server** owns the count
 * now, and the client keeps it current only across filter changes that never
 * reach the server (those are `pushState`, not navigations, so nothing
 * re-asserts over them).
 *
 * `total` absent means the search failed or hasn't answered — the count is
 * dropped rather than printed as `0`, because no answer is not an answer of
 * none. `page` is 0-based in the URL and 1-based in prose; naming it keeps 97
 * pages of San Jose from sharing one title.
 */
export function searchPageTitle(filters: SearchFilters, total?: number): string {
  const where = searchWhere(filters);
  const noun =
    typeof total === 'number' && total > 0
      ? `${total.toLocaleString()} ${total === 1 ? 'home' : 'homes'}`
      : 'homes';
  const subject = `${noun} for sale in ${where}`;
  const scoped = filters.q
    ? `“${filters.q}” — ${subject}`
    : subject.charAt(0).toUpperCase() + subject.slice(1);
  return filters.page > 0 ? `${scoped} · Page ${filters.page + 1}` : scoped;
}

/**
 * Human label for the sqft filter, or undefined when none is set.
 *
 * The Sqft popover only offers a *minimum*, so `sqftMax` can only arrive from a
 * shared or hand-edited URL — and it was applied while the pill and the chip
 * both read only "9,000+ sqft". A link carrying `sqftMin=9000&sqftMax=500`
 * therefore showed zero homes with the contradiction completely invisible.
 * (W12 R117)
 */
export function sqftLabel(filters: SearchFilters): string | undefined {
  const { sqftMin: lo, sqftMax: hi } = filters;
  if (lo !== undefined && hi !== undefined) {
    return `${lo.toLocaleString()}–${hi.toLocaleString()} sqft`;
  }
  if (lo !== undefined) return `${lo.toLocaleString()}+ sqft`;
  if (hi !== undefined) return `Up to ${hi.toLocaleString()} sqft`;
  return undefined;
}

/**
 * Ranges whose minimum is above their maximum — filters that cannot match
 * anything, whatever the inventory. Each is individually valid, so the schema
 * accepts them; a shared or hand-edited URL is how they arrive. Worth naming in
 * the empty state, because "No homes match these filters" sends a buyer hunting
 * through a filter set that is arithmetically impossible. (W12 R117)
 */
export function rangeContradictions(
  filters: SearchFilters
): { key: 'price' | 'sqft'; label: string; clear: Partial<SearchFilters> }[] {
  const out: { key: 'price' | 'sqft'; label: string; clear: Partial<SearchFilters> }[] = [];
  const { priceMin, priceMax, sqftMin, sqftMax } = filters;
  if (priceMin !== undefined && priceMax !== undefined && priceMin > priceMax) {
    out.push({
      key: 'price',
      label: 'price range',
      clear: { priceMin: undefined, priceMax: undefined },
    });
  }
  if (sqftMin !== undefined && sqftMax !== undefined && sqftMin > sqftMax) {
    out.push({
      key: 'sqft',
      label: 'size range',
      clear: { sqftMin: undefined, sqftMax: undefined },
    });
  }
  return out;
}

export function propertyTypesFromFilters(filters: SearchFilters): PropertyType[] {
  // NO WHITELIST. The feed's vocabulary is whatever the MLS sends, and it is wider
  // than the four types this app used to know about: live, 39 homes sit in
  // "other residential", "farm / ranch" and "manufactured home", and the raw keys
  // appear in deep links the city pages themselves publish.
  //
  // Filtering against a hardcoded list SILENTLY DROPPED those, so a published link
  // for "Farm / Ranch in Gilroy" arrived, lost its only filter, and rendered every
  // home in Gilroy under a heading that said otherwise. A dropped filter is worse
  // than a rejected one: nothing on the page says the request was not honoured.
  //
  // The backend has no allowlist either — it filters happily on "farm / ranch" — so
  // passing an unknown value through yields an honest empty result rather than a
  // wrong one.
  return (filters.types ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((t): t is PropertyType => t.length > 0);
}

/** Map URL filters to the search API request params (bbox is merged in by the client).
 * City slugs become space-separated names: Meilisearch's filter match is
 * case-insensitive but "san-jose" would not match "SAN JOSE". */
export function filtersToApiParams(filters: SearchFilters, perPage = 20): SearchParams {
  const cities = citySlugsFromFilters(filters).map((slug) => slug.replace(/-/g, ' '));
  const propertyTypes = propertyTypesFromFilters(filters);
  return {
    q: filters.q,
    priceMin: filters.priceMin,
    priceMax: filters.priceMax,
    bedsMin: filters.bedsMin,
    bathsMin: filters.bathsMin,
    sqftMin: filters.sqftMin,
    sqftMax: filters.sqftMax,
    cities: cities.length ? cities : undefined,
    propertyTypes: propertyTypes.length ? propertyTypes : undefined,
    yearBuiltMin: filters.yearMin,
    openHousesOnly: filters.oh === '1' || undefined,
    priceCutsOnly: filters.cut === '1' || undefined,
    hoaMax: filters.hoaMax,
    domMax: filters.domMax,
    zips: filters.zips ? filters.zips.split(',') : undefined,
    sort: filters.sort,
    page: filters.page,
    perPage,
  };
}

/** Serialize filters to a canonical query string, omitting defaults. Returns '' or '?...'. */
export function filtersToQueryString(filters: SearchFilters): string {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.cities) params.set('cities', filters.cities);
  if (filters.priceMin !== undefined) params.set('priceMin', String(filters.priceMin));
  if (filters.priceMax !== undefined) params.set('priceMax', String(filters.priceMax));
  if (filters.bedsMin !== undefined) params.set('bedsMin', String(filters.bedsMin));
  if (filters.bathsMin !== undefined) params.set('bathsMin', String(filters.bathsMin));
  if (filters.sqftMin !== undefined) params.set('sqftMin', String(filters.sqftMin));
  if (filters.sqftMax !== undefined) params.set('sqftMax', String(filters.sqftMax));
  if (filters.types) params.set('types', filters.types);
  if (filters.yearMin !== undefined) params.set('yearMin', String(filters.yearMin));
  if (filters.oh) params.set('oh', '1');
  if (filters.cut) params.set('cut', '1');
  if (filters.hoaMax !== undefined) params.set('hoaMax', String(filters.hoaMax));
  if (filters.domMax !== undefined) params.set('domMax', String(filters.domMax));
  if (filters.zips) params.set('zips', filters.zips);
  if (filters.bbox) params.set('bbox', filters.bbox);
  if (filters.sort !== 'newest') params.set('sort', filters.sort);
  if (filters.page > 0) params.set('page', String(filters.page));
  if (filters.view !== DEFAULT_VIEW) params.set('view', filters.view);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * The same query string with presentation-only params stripped — the identity of
 * *which homes match*, as opposed to how they're arranged on screen.
 *
 * `view` has to live in the URL (shareable, survives a filter change, Back
 * undoes it) but it must not leak into the React Query key or the SEO canonical:
 * flipping list ⇄ map would otherwise evict the cache and refetch the identical
 * twenty homes, and every search would mint three near-duplicate URLs for
 * crawlers. (W13 R121)
 */
export function filtersToResultsKey(filters: SearchFilters): string {
  return filtersToQueryString({ ...filters, view: DEFAULT_VIEW });
}
